import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, ViewChild } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BaseChartDirective } from 'ng2-charts';
import { Chart, registerables, ChartConfiguration } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import html2canvas from 'html2canvas';
import { Store } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import { take, switchMap, tap, takeUntil } from 'rxjs/operators';

import { AppState } from '../../../../../../state/app.state';
import { loadSidenavData } from '../../../../../../state/action';
import { selectSidenavDataByTabIdAndKey } from '../../../../../../state/selectors';
import { GeneralInnovationActivityResultsService } from '../../../../services/general-innovation-activity-results.service';
import { IInnovationMetricsHistoryPoint } from '../../../../../../core/models/results.model';
import { ICompany } from '../../../../../../core/models/company.model';
import { SIGNAL_COLORS } from '../../../../../../shared/utils/bracket.utils';
import {
  FONT_FAMILY, CHART_COLOR, TimeRange, axisLabel, periodLabel, filterQuarterly,
  whiteBackgroundPlugin, chartAreaBgPlugin, categoryXScale, baseTooltip, categoryZoom, COMMON_CHART_OPTIONS,
} from '../../../../../../shared/utils/chart-style';

export type SecondaryAxis = 'innovation_score' | 'invention_count_120m' | 'relative_growth_12m' | 'quality_score_12m';

interface SecondaryAxisCfg {
  label: string;
  field: keyof IInnovationMetricsHistoryPoint;
  color: string;
  type: 'scatter' | 'bar';
  hoverSuffix: string;
}

const SECONDARY_AXIS_CONFIG: Record<SecondaryAxis, SecondaryAxisCfg> = {
  innovation_score: { label: 'Innovation Score', field: 'innovation_score', color: '#3b82f6', type: 'scatter', hoverSuffix: '' },
  invention_count_120m: { label: 'Portfolio Size (10y)', field: 'invention_count_120m', color: '#9EDEB8', type: 'bar', hoverSuffix: '' },
  relative_growth_12m: { label: 'Growth (%)', field: 'relative_growth_12m', color: '#F6D08F', type: 'bar', hoverSuffix: '%' },
  quality_score_12m: { label: 'Quality Score', field: 'quality_score_12m', color: '#a855f7', type: 'scatter', hoverSuffix: '' },
};

Chart.register(...registerables, zoomPlugin);

@Component({
  selector: 'app-innovation-metrics-activity',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, BaseChartDirective],
  templateUrl: './innovation-metrics-activity.component.html',
  styleUrl: './innovation-metrics-activity.component.css',
})
export class InnovationMetricsActivityComponent implements OnChanges, OnDestroy {
  @Input() tabId!: string;
  @Input() entityids: string[] | null = null;
  @Input() tab_name: string | null = null;
  @Input() entity_data: ICompany | null = null;
  @Input() printMode = false;             // PDF report: lock axis, hide controls
  @Input() lockedAxis?: SecondaryAxis;

  @ViewChild(BaseChartDirective) private chartDirective?: BaseChartDirective;
  @ViewChild('exportTarget') private exportTarget?: ElementRef<HTMLDivElement>;

  private destroy$ = new Subject<void>();

  history: IInnovationMetricsHistoryPoint[] = [];

  chartjsData!: ChartConfiguration<'bar'>['data'];
  chartjsOptions!: ChartConfiguration<'bar'>['options'];
  readonly chartPlugins = [whiteBackgroundPlugin, chartAreaBgPlugin];

  showChart = false;
  primaryLabel = 'Stock Performance (Indexed)';
  hasMcap = true;

  secondaryAxis: SecondaryAxis | null = 'innovation_score';
  timeRange: TimeRange = '10y';

  readonly SIGNAL = SIGNAL_COLORS;
  readonly SECONDARY_CFG = SECONDARY_AXIS_CONFIG;

  showSpinner = false;
  showDataMissing = false;
  loadingFailed = false;
  isUnsupportedCompanyType = false;

  private readonly SUPPORTED_COMPANY_TYPES = ['Public Company', 'Public Investment Firm'];

  constructor(
    private store: Store<AppState>,
    private apiService: GeneralInnovationActivityResultsService,
  ) { }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.printMode && this.lockedAxis !== undefined) {
      this.secondaryAxis = this.lockedAxis;
    }
    if (changes['tabId']) {
      this.reset();
      this.loadHistory();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get chartCardTitle(): string {
    return this.secondaryAxis
      ? `Stock Performance vs ${SECONDARY_AXIS_CONFIG[this.secondaryAxis].label}`
      : 'Stock Performance vs Innovation Signal';
  }

  private reset(): void {
    this.history = [];
    this.hasMcap = true;
    this.showChart = false;
    this.showSpinner = false;
    this.showDataMissing = false;
    this.loadingFailed = false;
    this.isUnsupportedCompanyType = false;
  }

  private loadHistory(): void {
    const entityid = this.entityids?.[0];
    if (!entityid) {
      this.showDataMissing = true;
      return;
    }

    const companyType = this.entity_data?.companytype;
    if (companyType && !this.SUPPORTED_COMPANY_TYPES.includes(companyType)) {
      this.isUnsupportedCompanyType = true;
      return;
    }

    const currentTabId = this.tabId;
    this.showSpinner = true;

    this.store.select(selectSidenavDataByTabIdAndKey(currentTabId, 'innov_metrics_history')).pipe(
      take(1),
      switchMap((cached) => {
        if (cached) {
          if (this.tabId === currentTabId) {
            this.history = cached;
            this.hasMcap = cached.some((p: IInnovationMetricsHistoryPoint) => p.stockprice_indexed > 0);
            this.buildChart();
            this.showSpinner = false;
          }
          return of(null);
        }
        return this.apiService.getInnovationHistory(entityid!, currentTabId).pipe(
          takeUntil(this.destroy$),
          tap((res) => {
            const payload = res.body?.payload;
            if (!res.ok || !payload?.history?.length) {
              if (this.tabId === currentTabId) {
                this.showSpinner = false;
                this.showDataMissing = true;
              }
              return;
            }
            const targetTabId = payload.tabId ?? currentTabId;
            const historyData = payload.history;
            this.store.dispatch(loadSidenavData({ tabId: targetTabId, sidenavKey: 'innov_metrics_history', data: historyData }));
            if (this.tabId === currentTabId && targetTabId === currentTabId) {
              this.showSpinner = false;
              this.history = historyData;
              this.hasMcap = historyData.some(p => p.stockprice_indexed > 0);
              this.buildChart();
            }
          }),
        );
      }),
    ).subscribe({
      error: () => {
        if (this.tabId === currentTabId) {
          this.showSpinner = false;
          this.loadingFailed = true;
        }
      },
    });
  }

  toggleSecondaryAxis(axis: SecondaryAxis): void {
    this.secondaryAxis = this.secondaryAxis === axis ? null : axis;
    if (this.history.length) this.buildChart();
  }

  setTimeRange(range: TimeRange): void {
    this.timeRange = range;
    if (this.history.length) this.buildChart();
  }

  resetZoom(): void {
    this.chartDirective?.chart?.resetZoom();
  }

  /** Exact chart image (Chart.js native export) for the server-rendered PDF report.
   *  printMode instances bake in the right-side legend (see buildChart). */
  getChartImage(scale = 2): string | null {
    return this.chartDirective?.chart?.toBase64Image('image/png', scale) ?? null;
  }

  async downloadPng(): Promise<void> {
    const node = this.exportTarget?.nativeElement;
    if (!node) return;
    const canvas = await html2canvas(node, {
      backgroundColor: '#ffffff',
      scale: 2,
      useCORS: true,
    });
    const safeName = (this.tab_name || 'signal-history').replace(/[^a-z0-9]+/gi, '-').toLowerCase();
    const a = document.createElement('a');
    a.href = canvas.toDataURL('image/png');
    a.download = `${safeName}-signal-history.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  private getFilteredHistory(): IInnovationMetricsHistoryPoint[] {
    return filterQuarterly(this.history, this.timeRange);
  }

  private buildChart(): void {
    const filtered = this.getFilteredHistory();
    if (!filtered.length) {
      this.showDataMissing = true;
      return;
    }

    this.primaryLabel = 'Stock Performance (Indexed)';
    const labels = filtered.map(p => axisLabel(p.date_eom));
    const primaryData = filtered.map(p => p.stockprice_indexed);
    const signalAt = (i: number): string | undefined => filtered[i]?.signal;

    const datasets: any[] = [];

    if (this.secondaryAxis) {
      const cfg = SECONDARY_AXIS_CONFIG[this.secondaryAxis];
      const secData = filtered.map(p => (p[cfg.field] as number | null) ?? null);

      if (cfg.type === 'bar') {
        datasets.push({
          type: 'bar',
          label: cfg.label,
          data: secData,
          yAxisID: 'y2',
          backgroundColor: cfg.color,
          borderColor: cfg.color,
          borderWidth: 0,
          borderRadius: 4,
          borderSkipped: false,
          categoryPercentage: 0.85,
          barPercentage: 0.7,
          order: 3,
        });
      } else {
        datasets.push({
          type: 'line',
          label: cfg.label,
          data: secData,
          yAxisID: 'y2',
          borderColor: cfg.color,
          borderWidth: 2,
          tension: 0.35,
          cubicInterpolationMode: 'monotone',
          pointRadius: 0,
          pointHoverRadius: 4,
          pointBackgroundColor: cfg.color,
          fill: false,
          order: 3,
        });
      }
    }

    // Signal markers on the primary line: BUY/SELL big triangles (SELL flipped), HOLD circle.
    const radiusBySignal = (sig: string | undefined): number => {
      if (sig === 'BUY' || sig === 'SELL') return 7;
      if (sig === 'HOLD') return 4;
      return 0;
    };

    datasets.push({
      type: 'line',
      label: this.primaryLabel,
      data: primaryData,
      yAxisID: 'y',
      borderColor: CHART_COLOR.scoreLine,
      borderWidth: 2,
      tension: 0.35,
      cubicInterpolationMode: 'monotone',
      pointRadius: (ctx: any) => radiusBySignal(signalAt(ctx.dataIndex)),
      pointHoverRadius: (ctx: any) => radiusBySignal(signalAt(ctx.dataIndex)) + 1,
      pointStyle: (ctx: any) => (signalAt(ctx.dataIndex) === 'HOLD' ? 'circle' : 'triangle'),
      pointRotation: (ctx: any) => (signalAt(ctx.dataIndex) === 'SELL' ? 180 : 0),
      pointBackgroundColor: (ctx: any) => {
        const sig = signalAt(ctx.dataIndex);
        if (sig === 'BUY') return SIGNAL_COLORS.BUY;
        if (sig === 'HOLD') return SIGNAL_COLORS.HOLD;
        if (sig === 'SELL') return SIGNAL_COLORS.SELL;
        return CHART_COLOR.scoreLine;
      },
      pointBorderColor: '#ffffff',
      pointBorderWidth: (ctx: any) => (signalAt(ctx.dataIndex) ? 1 : 0),
      fill: false,
      spanGaps: true,
      order: 2,
    });

    this.chartjsData = { labels, datasets } as any;

    const yScale: any = {
      position: 'left',
      beginAtZero: true,
      title: {
        display: true,
        text: this.primaryLabel,
        font: { family: FONT_FAMILY, size: 14, weight: 500 },
        color: CHART_COLOR.axisTitle,
        padding: { bottom: 8 },
      },
      ticks: {
        font: { family: FONT_FAMILY, size: 14 },
        color: CHART_COLOR.axisText,
        padding: 8,
      },
      // grid: { color: CHART_COLOR.grid, z: -1 },   // z:-1 → gridlines behind the bars (always on)
      grid: { display: this.printMode, color: CHART_COLOR.grid, z: -1 },   // print-only gridlines
      border: { display: false },
    };

    const scales: any = { x: categoryXScale(), y: yScale };
    scales.x.grid.display = this.printMode;   // print-only gridlines

    if (this.secondaryAxis) {
      const cfg = SECONDARY_AXIS_CONFIG[this.secondaryAxis];
      const isBounded = this.secondaryAxis === 'innovation_score' || this.secondaryAxis === 'quality_score_12m';
      scales.y2 = {
        position: 'right',
        min: isBounded ? 0 : undefined,
        max: isBounded ? 100 : undefined,
        title: {
          display: true,
          text: cfg.label,
          font: { family: FONT_FAMILY, size: 14, weight: 500 },
          color: CHART_COLOR.axisTitle,   // neutral dark text (series color kept on trace only)
          padding: { bottom: 8 },
        },
        ticks: {
          maxTicksLimit: 6,
          font: { family: FONT_FAMILY, size: 11 },
          color: CHART_COLOR.axisText,   // neutral dark text
          padding: 8,
          callback: (v: number | string) => Math.round(Number(v)).toLocaleString(),
        },
        grid: { display: false },
        border: { display: false },
      };
    }

    const cfg = this.secondaryAxis ? SECONDARY_AXIS_CONFIG[this.secondaryAxis] : null;
    this.chartjsOptions = {
      ...COMMON_CHART_OPTIONS,
      plugins: {
        // PDF export (printMode) shows a right-side legend incl. Buy/Hold/Sell markers; UI keeps its own HTML legend.
        legend: this.printMode ? {
          display: true, position: 'right',
          labels: {
            font: { family: FONT_FAMILY, size: 12 }, color: CHART_COLOR.axisText, usePointStyle: true, boxWidth: 14,
            generateLabels: () => [
              { text: 'Stock Performance', strokeStyle: CHART_COLOR.scoreLine, fillStyle: CHART_COLOR.scoreLine, lineWidth: 2, pointStyle: 'line', fontColor: CHART_COLOR.axisText },
              { text: 'Buy', strokeStyle: this.SIGNAL.BUY, fillStyle: this.SIGNAL.BUY, pointStyle: 'triangle', fontColor: CHART_COLOR.axisText },
              { text: 'Hold', strokeStyle: this.SIGNAL.HOLD, fillStyle: this.SIGNAL.HOLD, pointStyle: 'circle', fontColor: CHART_COLOR.axisText },
              { text: 'Sell', strokeStyle: this.SIGNAL.SELL, fillStyle: this.SIGNAL.SELL, pointStyle: 'triangle', fontColor: CHART_COLOR.axisText },
              ...(cfg ? [{ text: cfg.label, strokeStyle: cfg.color, fillStyle: cfg.color, lineWidth: 2, pointStyle: (cfg.type === 'bar' ? 'rect' : 'line'), fontColor: CHART_COLOR.axisText }] : []),
            ] as any,
          },
        } : { display: false },
        tooltip: baseTooltip({
          title: (items: any[]) => periodLabel(filtered[items[0].dataIndex].date_eom),
          label: (item: any) => {
            const v = item.parsed.y;
            if (v == null) return '';
            const dsLabel = item.dataset.label ?? '';
            if (dsLabel === this.primaryLabel) {
              return ` ${dsLabel}: ${Math.round(v)}`;
            }
            const cfg = this.secondaryAxis ? SECONDARY_AXIS_CONFIG[this.secondaryAxis] : null;
            const suffix = cfg?.hoverSuffix ?? '';
            return ` ${dsLabel}: ${Math.round(v)}${suffix}`;
          },
          afterBody: (items: any[]) => {
            const sig = signalAt(items[0].dataIndex);
            if (sig !== 'BUY' && sig !== 'HOLD' && sig !== 'SELL') return '';
            const glyph = sig === 'BUY' ? '▲' : sig === 'SELL' ? '▼' : '●';
            return `${glyph} Signal: ${sig}`;
          },
        }),
        zoom: categoryZoom(),
      },
      scales,
    } as any;

    this.showChart = true;
  }
}
